import { useState } from 'react';
import { MapPin, Phone, Clock, Send, CheckCircle } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import Divider from '../components/Divider';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      await axios.post(`${API}/contact`, formData);
      setIsSubmitted(true);
      setFormData({ name: '', email: '', message: '' });
    } catch (err) {
      console.error('Error submitting form:', err);
      setError('Failed to send message. Please try again or call us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <main className="pt-24 min-h-screen bg-white" data-testid="contact-page">
      {/* Hero Section */}
      <section className="py-16 px-6 text-center bg-gray-50">
        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold text-black mb-4">
          CONTACT <span className="text-brand-red">US</span>
        </h1>
        <p className="text-gray-600 max-w-2xl mx-auto">
          We'd love to hear from you. Get in touch with us for reservations, inquiries, or feedback.
        </p>
      </section>

      <Divider className="py-6" />

      {/* Contact Content */}
      <section className="py-16 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Info */}
            <div data-testid="contact-info">
              <h2 className="font-serif text-3xl font-bold text-black mb-8">
                Get In <span className="text-brand-red">Touch</span>
              </h2>

              <div className="space-y-8">
                <div className="flex items-start gap-4 group">
                  <div className="w-14 h-14 flex items-center justify-center border-2 border-gray-200 group-hover:border-brand-red group-hover:bg-brand-red/5 transition-all duration-300 flex-shrink-0">
                    <MapPin className="text-brand-red" size={24} />
                  </div>
                  <div>
                    <h3 className="font-serif text-xl font-semibold text-black mb-2">Address</h3>
                    <p className="text-gray-600">
                      4 Cumberland Street<br />
                      Luton LU1 3BW<br />
                      United Kingdom
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4 group">
                  <div className="w-14 h-14 flex items-center justify-center border-2 border-gray-200 group-hover:border-brand-red group-hover:bg-brand-red/5 transition-all duration-300 flex-shrink-0">
                    <Phone className="text-brand-red" size={24} />
                  </div>
                  <div>
                    <h3 className="font-serif text-xl font-semibold text-black mb-2">Phone</h3>
                    <a 
                      href="tel:01582517786" 
                      className="text-gray-600 hover:text-brand-red transition-colors text-lg"
                    >
                      01582 517786
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4 group">
                  <div className="w-14 h-14 flex items-center justify-center border-2 border-gray-200 group-hover:border-brand-red group-hover:bg-brand-red/5 transition-all duration-300 flex-shrink-0">
                    <Clock className="text-brand-red" size={24} />
                  </div>
                  <div>
                    <h3 className="font-serif text-xl font-semibold text-black mb-2">Opening Hours</h3>
                    <p className="text-gray-600">
                      Open Daily<br />
                      1PM - 11:30PM
                    </p>
                  </div>
                </div>
              </div>

              {/* Quick Call CTA */}
              <div className="mt-10 p-6 bg-gray-50 border border-gray-200">
                <p className="text-gray-600 mb-4">Prefer to call? We're happy to help!</p>
                <a 
                  href="tel:01582517786"
                  data-testid="contact-call-btn"
                  className="inline-flex items-center gap-2 bg-brand-red text-white px-8 py-3 font-semibold text-sm tracking-wider uppercase hover:bg-red-700 transition-all duration-300"
                >
                  <Phone size={18} />
                  <span>Call Now</span>
                </a>
              </div>
            </div>

            {/* Contact Form */}
            <div data-testid="contact-form-section">
              <h2 className="font-serif text-3xl font-bold text-black mb-8">
                Send a <span className="text-brand-red">Message</span>
              </h2>

              {isSubmitted ? (
                <div className="bg-gray-50 border border-gray-200 p-10 text-center" data-testid="form-success">
                  <CheckCircle className="text-green-500 w-16 h-16 mx-auto mb-6" />
                  <h3 className="font-serif text-2xl font-bold text-black mb-4">Message Sent!</h3>
                  <p className="text-gray-600 mb-6">
                    Thank you for contacting us. We'll get back to you as soon as possible.
                  </p>
                  <button
                    onClick={() => setIsSubmitted(false)}
                    className="text-brand-red hover:text-red-700 transition-colors text-sm uppercase tracking-wider"
                  >
                    Send Another Message
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                  <div>
                    <Label htmlFor="name" className="text-gray-700 text-sm uppercase tracking-wider mb-2 block">
                      Your Name
                    </Label>
                    <Input
                      id="name"
                      name="name"
                      type="text"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      minLength={2}
                      data-testid="input-name"
                      className="bg-white border-gray-300 text-black placeholder:text-gray-400 focus:border-brand-red rounded-none h-12"
                      placeholder="Enter your name"
                    />
                  </div>

                  <div>
                    <Label htmlFor="email" className="text-gray-700 text-sm uppercase tracking-wider mb-2 block">
                      Email Address
                    </Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      data-testid="input-email"
                      className="bg-white border-gray-300 text-black placeholder:text-gray-400 focus:border-brand-red rounded-none h-12"
                      placeholder="Enter your email"
                    />
                  </div>

                  <div>
                    <Label htmlFor="message" className="text-gray-700 text-sm uppercase tracking-wider mb-2 block">
                      Your Message
                    </Label>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      minLength={10}
                      rows={6}
                      data-testid="input-message"
                      className="bg-white border-gray-300 text-black placeholder:text-gray-400 focus:border-brand-red rounded-none resize-none"
                      placeholder="How can we help you?"
                    />
                  </div>

                  {error && (
                    <p className="text-red-500 text-sm" data-testid="form-error">{error}</p>
                  )}

                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    data-testid="submit-btn"
                    className="w-full bg-brand-red text-white hover:bg-red-700 font-semibold text-sm tracking-wider uppercase h-12 rounded-none transition-all duration-300 disabled:opacity-50"
                  >
                    {isSubmitting ? (
                      <span className="flex items-center gap-2">
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        Sending...
                      </span>
                    ) : (
                      <span className="flex items-center gap-2">
                        <Send size={18} />
                        Send Message
                      </span>
                    )}
                  </Button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      <Divider />

      {/* Map Section */}
      <section className="py-16 px-6 bg-gray-50" data-testid="map-section">
        <div className="max-w-6xl mx-auto">
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-center text-black mb-4">
            FIND <span className="text-brand-red">US</span>
          </h2>
          <p className="text-gray-600 text-center max-w-2xl mx-auto mb-12">
            Located in the heart of Luton, we're easy to find and always ready to welcome you.
          </p>

          <div className="border-4 border-brand-red/20 overflow-hidden">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2454.8961776091736!2d-0.4193289!3d51.8804559!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x48764419b9c3fe9f%3A0x6a893fdb35d7e1e6!2s4%20Cumberland%20St%2C%20Luton%20LU1%203BW%2C%20UK!5e0!3m2!1sen!2s!4v1704067200000!5m2!1sen!2s"
              width="100%"
              height="450"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Butt Karahi Luton Location"
              data-testid="google-map"
            ></iframe>
          </div>
        </div>
      </section>
    </main>
  );
};

export default Contact;
